// Relatório semanal da alimentação: o planejado e o que foi comido (com as alterações), e os totais por alimento.
import { useMemo, useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import { useDoc } from '../../store/store';
import { semanaAlimentar } from '../../store/bemestar';
import { segunda } from '../../store/estudos';
import type { Item } from '../../store/types';
import { gerarECompartilharPdf, type BlocoPdf } from '../../lib/pdf';
import { semBloqueio } from '../../auth/AuthContext';
import { Btn, Chip, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { addDays, brYmd, longDate, today } from '../../lib/date';
import { qtdTxt } from './EscolherAlimento';
import { UNIDADES } from '../../lib/bemestarCatalogo';
import { t, tn } from '../../lib/i18n';

const lista = (l: Item[]) => l.map(i => [qtdTxt(i), i.text].filter(Boolean).join(' ')).join(', ');
const total = (n: number, un: string) => {
  const v = String(Math.round(n * 10) / 10).replace('.', ',');
  return un === 'g' || un === 'ml' ? `${v} ${un}` : un === 'un' ? v : `${v} ${tn(UNIDADES.find(u => u.id === un)?.nome ?? un, Number(String(v).replace(',', '.')))}`;
};

export function RelatorioSemana({ visible, cor, onClose }: { visible: boolean; cor: string; onClose: () => void }) {
  const d = useDoc('bemestar');
  const [seg, setSeg] = useState(segunda(today()));
  const [parte, setParte] = useState<'dias' | 'totais'>('dias');
  const r = useMemo(() => semanaAlimentar(d, seg), [d, seg]);
  const titulo = t('Semana de {de} a {ate}', { de: brYmd(seg), ate: brYmd(addDays(seg, 6)) });

  const pdf = () => {
    const b: BlocoPdf[] = [{ t: 'titulo', texto: t('Alimentação') + ' · ' + titulo }, { t: 'sub', texto: t('Planejado e comido') }];
    b.push({ t: 'tabela', colunas: [t('Dia'), t('Refeição'), t('Planejado'), t('Comido / alterações')], larguras: [0.14, 0.18, 0.34, 0.34],
      linhas: r.linhas.map(l => [brYmd(l.data).slice(0, 5), l.ref.nome, lista(l.planejado) || '-',
        [l.mudou ? lista(l.comido) : l.feita ? t('como planejado') : '-', l.texto].filter(Boolean).join(' · ')]) });
    b.push({ t: 'sub', texto: t('Totais por alimento') }, { t: 'tabela', colunas: [t('Alimento'), t('Planejado'), t('Comido')], larguras: [0.5, 0.25, 0.25],
      linhas: r.totais.map(x => [x.nome, total(x.plan, x.un), total(x.com, x.un)]) });
    semBloqueio(() => gerarECompartilharPdf(t('Alimentação {semana}', { semana: brYmd(seg) }), b, 'My Planner'))
      .catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)));
  };

  return (
    <Sheet visible={visible} onClose={onClose} title={t('Relatório da semana')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Pressable hitSlop={10} onPress={() => setSeg(addDays(seg, -7))}><Text style={{ color: C.sub, fontSize: 22 }}>‹</Text></Pressable>
          <Text style={{ color: C.text, fontWeight: '700', flex: 1, textAlign: 'center' }}>{titulo}</Text>
          <Pressable hitSlop={10} onPress={() => setSeg(addDays(seg, 7))}><Text style={{ color: C.sub, fontSize: 22 }}>›</Text></Pressable>
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip cor={cor} on={parte === 'dias'} label={t('Planejado e comido')} onPress={() => setParte('dias')} />
          <Chip cor={cor} on={parte === 'totais'} label={t('Totais por alimento')} onPress={() => setParte('totais')} />
        </View>
        {!r.linhas.length && <Text style={{ color: C.sub }}>{t('Monte o cronograma alimentar para ver o relatório.')}</Text>}

        {parte === 'dias' && r.dias.map(dia => {
          const ls = r.linhas.filter(l => l.data === dia);
          if (!ls.length) return null;
          return (
            <View key={dia} style={{ gap: 6 }}>
              <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', marginTop: 6 }}>{longDate(dia)}</Text>
              {ls.map(l => (
                <View key={l.ref.id} style={{ backgroundColor: l.mudou ? alpha(C.warn, 0.12) : C.card, borderRadius: 12, padding: 10, gap: 3 }}>
                  <Text style={{ color: C.text, fontWeight: '700' }}>{l.ref.icon} {l.ref.nome} {l.feita ? '✓' : ''}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }}>{t('Planejado')}: {lista(l.planejado) || '-'}</Text>
                  {l.mudou ? <Text style={{ color: C.warn, fontSize: 12 }}>{t('Comido')}: {l.comido.length ? lista(l.comido) : '-'}{l.texto ? ` · ${l.texto}` : ''}</Text>
                    : dia <= today() && <Text style={{ color: l.feita ? C.ok : C.mute, fontSize: 12 }}>{l.feita ? t('Como planejado') : t('Não marcada como feita')}</Text>}
                </View>
              ))}
            </View>
          );
        })}

        {parte === 'totais' && (<>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Comido conta as refeições marcadas como feitas (ou com alterações) até hoje.')}</Text>
          <View style={{ flexDirection: 'row', paddingHorizontal: 10 }}>
            <Text style={{ color: C.sub, fontSize: 11, flex: 2 }}>{t('Alimento')}</Text>
            <Text style={{ color: C.sub, fontSize: 11, flex: 1, textAlign: 'right' }}>{t('Planejado')}</Text>
            <Text style={{ color: C.sub, fontSize: 11, flex: 1, textAlign: 'right' }}>{t('Comido')}</Text>
          </View>
          {r.totais.map(x => (
            <View key={x.nome + x.un} style={{ flexDirection: 'row', backgroundColor: C.card, borderRadius: 10, padding: 10 }}>
              <Text style={{ color: C.text, flex: 2 }}>{x.nome}</Text>
              <Text style={{ color: C.sub, flex: 1, textAlign: 'right' }}>{total(x.plan, x.un)}</Text>
              <Text style={{ color: C.text, fontWeight: '700', flex: 1, textAlign: 'right' }}>{total(x.com, x.un)}</Text>
            </View>
          ))}
        </>)}
        {!!r.linhas.length && <Btn outline cor={cor} label={'📄 ' + t('Gerar PDF')} onPress={pdf} />}
      </ScrollView>
    </Sheet>
  );
}
